// 通道组合详情页的换算（docs/10 第 3 节，页面说明见 docs/04“通道组合详情页面”）：把通道 × 运营商的指标快照合并到组的成员上，推算各策略下现在会选谁。
// 指标沿用通道 × 运营商的全部流量，不按组区分（与主备的统计口径相同，决策 D92）；单条消息还要过内容、sender 等硬过滤，这里只看通道状态。
import type { ChannelState } from "@/ui";
import type { ChannelHealth, CircuitState, PrimaryBackupList, WindowMetrics } from "./health";
import type { Channel, ChannelGroup, CostPrice, CurrencyRate, GroupMember } from "./master";

/** 运营商键 "mcc-mnc"；null 表示全部运营商合并。 */
export type OpKey = string | null;

/** 指标行或主备状态行的运营商键。 */
export const opKey = (r: { mcc: string; mnc: string }) => `${r.mcc}-${r.mnc}`;

/** 一个成员在所选运营商上的指标：多个运营商、多个通道按样本数加权合并，TPS 相加。 */
export interface MemberStats {
  recent: WindowMetrics; hour: WindowMetrics;
  adjusted?: { delivery_rate: number; samples: number };
  tps: number;
  /** 最差的熔断状态，与熔断起始时刻。 */
  circuit: CircuitState; circuit_since?: string;
}

/** 选路上的可用性（docs/10 3.2）：ok 可正常选出；fallback 熔断中或半开，只在都被挡时降级选出；off 被硬过滤（停用、摘除）。 */
export type Usability = "ok" | "fallback" | "off";

/** 详情表的一行：成员本身、指标与可用性；inSlot 表示时段包含现在（只有 time 策略的成员有时段）。 */
export interface MemberRow {
  member: GroupMember;
  channel?: Channel; group?: ChannelGroup;
  stats: MemberStats | null;
  state: ChannelState;
  usable: Usability;
  inSlot: boolean;
}

/** 推算用的上下文：所选运营商与北京时间的当天分钟数。 */
export interface Scope { items?: ChannelHealth[]; op: OpKey; minute: number }

const CIRCUIT_RANK: Record<CircuitState, number> = { closed: 0, limited: 1, half_open: 2, open: 3 };

/** 两个窗口的指标按样本数加权合并；没有样本的比率记 0，与快照一致。 */
function mergeWindow(ws: WindowMetrics[]): WindowMetrics {
  const sum = (f: (w: WindowMetrics) => number) => ws.reduce((s, w) => s + f(w), 0);
  const samples = sum((w) => w.sample_size), submits = sum((w) => w.submit_samples);
  const avg = (f: (w: WindowMetrics) => number, n: number, weight: (w: WindowMetrics) => number) => (n ? sum((w) => f(w) * weight(w)) / n : 0);
  return {
    sample_size: samples, submit_samples: submits,
    delivery_rate: avg((w) => w.delivery_rate, samples, (w) => w.sample_size),
    dlr_latency_ms: avg((w) => w.dlr_latency_ms, samples, (w) => w.sample_size),
    submit_error_rate: avg((w) => w.submit_error_rate, submits, (w) => w.submit_samples),
    submit_latency_ms: avg((w) => w.submit_latency_ms, submits, (w) => w.submit_samples),
  };
}

/** 合并若干个 通道 × 运营商 的指标；一行都没有返回 null（没有流量，也没有熔断状态）。 */
export function mergeStats(rows: ChannelHealth[]): MemberStats | null {
  if (!rows.length) return null;
  // 同样差的取起始最早的，状态起始表示异常已经持续了多久（决策 D186）
  const pick = (a: ChannelHealth, b: ChannelHealth) => {
    const d = CIRCUIT_RANK[b.circuit] - CIRCUIT_RANK[a.circuit];
    return d > 0 || (d === 0 && !!b.circuit_since && (!a.circuit_since || b.circuit_since < a.circuit_since)) ? b : a;
  };
  const worst = rows.reduce(pick);
  const adj = rows.filter((r) => r.adjusted);
  const adjSamples = adj.reduce((s, r) => s + r.adjusted!.samples, 0);
  return {
    recent: mergeWindow(rows.map((r) => r.recent)), hour: mergeWindow(rows.map((r) => r.hour)),
    adjusted: adjSamples ? { samples: adjSamples, delivery_rate: adj.reduce((s, r) => s + r.adjusted!.delivery_rate * r.adjusted!.samples, 0) / adjSamples } : undefined,
    tps: rows.reduce((s, r) => s + r.tps_actual, 0),
    circuit: worst.circuit, circuit_since: worst.circuit_since,
  };
}

/** 组往下展开到通道（去重，取并集）；看过的组不再看，有环也能结束。 */
export function groupChannels(id: number, groups: ChannelGroup[] | undefined, seen = new Set<number>()): number[] {
  const g = groups?.find((x) => x.id === id);
  if (!g || seen.has(id)) return [];
  seen.add(id);
  const ids = g.members.flatMap((m) => (m.channel_id ? [m.channel_id] : m.group_id ? groupChannels(m.group_id, groups, seen) : []));
  return [...new Set(ids)];
}

/** 这些通道在所选运营商上的指标行。 */
export function healthRows(items: ChannelHealth[] | undefined, channelIDs: number[], op: OpKey): ChannelHealth[] {
  const ids = new Set(channelIDs);
  return (items ?? []).filter((r) => ids.has(r.channel_id) && (op === null || opKey(r) === op));
}

/** 按 id 找通道：列表还没加载返回 undefined，加载了找不到（已删除）返回 null。 */
function findChannel(channels: Channel[] | undefined, id: number): Channel | null | undefined {
  return channels ? (channels.find((x) => x.id === id) ?? null) : undefined;
}

/** 通道的状态与可用性：停用、摘除是硬过滤；熔断中与半开只能降级选出；慢启动受限照常可选（docs/10 3.2，决策 D48、D89）。
 * 列表还没加载（channel 为 undefined）时只看熔断。 */
function channelState(channel: Channel | null | undefined, stats: MemberStats | null): [ChannelState, Usability] {
  if (channel === null || channel?.status === "disabled") return ["disabled", "off"];
  if (channel?.manually_removed) return ["removed", "off"];
  if (stats?.circuit === "open") return ["open", "fallback"];
  if (stats?.circuit === "half_open") return ["degraded", "fallback"];
  return [stats?.circuit === "limited" ? "degraded" : "healthy", "ok"];
}

/** 成员的时段是否包含现在。 */
const inSlot = (m: GroupMember, minute: number) => !!m.time_start && !!m.time_end && inRange(m.time_start, m.time_end, minute);

/** 组的可用性：停用的组选不出（docs/10 第 2 节）；time 组只看当前时段的那一个成员，其余取成员里最好的（docs/10 3.2 第 4 条）。
 * 环由保存时的快照校验拒绝，这里不再防。 */
function groupUsability(id: number, groups: ChannelGroup[] | undefined, channels: Channel[] | undefined, s: Scope): Usability {
  const g = groups?.find((x) => x.id === id);
  if (!g || g.status === "disabled") return "off";
  const members = g.strategy === "time" ? g.members.filter((m) => inSlot(m, s.minute)) : g.members;
  const each = members.map((m): Usability => (m.group_id
    ? groupUsability(m.group_id, groups, channels, s)
    : channelState(findChannel(channels, m.channel_id!), mergeStats(healthRows(s.items, [m.channel_id!], s.op)))[1]));
  return each.includes("ok") ? "ok" : each.includes("fallback") ? "fallback" : "off";
}

const GROUP_STATE: Record<Usability, ChannelState> = { ok: "healthy", fallback: "open", off: "disabled" };

/** 组的每个成员一行，按 priority 从小到大（相同的保持配置顺序）。 */
export function memberRows(g: ChannelGroup, groups: ChannelGroup[] | undefined, channels: Channel[] | undefined, s: Scope): MemberRow[] {
  const sorted = [...g.members].sort((a, b) => a.priority - b.priority);
  return sorted.map((member) => {
    const slot = inSlot(member, s.minute);
    if (member.group_id) {
      const usable = groupUsability(member.group_id, groups, channels, s);
      const stats = mergeStats(healthRows(s.items, groupChannels(member.group_id, groups), s.op));
      return { member, group: groups?.find((x) => x.id === member.group_id), stats, state: GROUP_STATE[usable], usable, inSlot: slot };
    }
    const channel = findChannel(channels, member.channel_id!);
    const stats = mergeStats(healthRows(s.items, [member.channel_id!], s.op));
    const [state, usable] = channelState(channel, stats);
    return { member, channel: channel ?? undefined, stats, state, usable, inSlot: slot };
  });
}

/** 按顺序取第一个能正常选出的；都不行时取第一个只能降级选出的（全部被挡不剔除，D48 第 4 条）；都被硬过滤返回 -1（无路由）。 */
export function firstPick(rows: MemberRow[]): number {
  const ok = rows.findIndex((r) => r.usable === "ok");
  return ok >= 0 ? ok : rows.findIndex((r) => r.usable === "fallback");
}

/** 失败重试的首发是否改发给重试成员（docs/10 4.2）：重试成员选得出，且首发成员选不出、或首发只能降级而重试成员健康。 */
export function retrySkipsFirst(first: Usability, retry: Usability): boolean {
  return retry !== "off" && (first === "off" || (first === "fallback" && retry === "ok"));
}

/** 这些成员组里出现过的运营商，按 mcc、mnc 排序。 */
export function groupOps(items: ChannelHealth[] | undefined, channelIDs: number[]): string[] {
  return [...new Set(healthRows(items, channelIDs, null).map(opKey))].sort();
}

/** 主备组在一个运营商上的状态行；没有表示主用。 */
export function pbRow(pb: PrimaryBackupList | undefined, groupID: number, op: string) {
  return pb?.items.find((r) => r.group_id === groupID && opKey(r) === op);
}

/** 已生效（effective_from 不晚于 now）的最新版本，同一时刻取 id 大的；没有返回 undefined。 */
function latest<T extends { id: number; effective_from: string }>(rows: T[], now: number): T | undefined {
  return rows.filter((r) => Date.parse(r.effective_from) <= now)
    .reduce<T | undefined>((a, b) => (!a || Date.parse(b.effective_from) > Date.parse(a.effective_from) || (b.effective_from === a.effective_from && b.id > a.id) ? b : a), undefined);
}

/** 通道在一个运营商上现在生效的成本；base 是基准币种微单位，没有汇率时为 null。 */
export interface CurrentCost { price: number; currency: string; base: number | null }

/** 现在生效的成本：精确 MNC 优先，没有再用不限 MNC 的；按当时的汇率（1 原币 = rate / 1e6 基准币，docs/03 3.7）截断换算，与 worker 的 billing.Quote 一致。 */
export function currentCost(prices: CostPrice[], rates: CurrencyRate[], channelID: number, op: string, now: number): CurrentCost | null {
  const [mcc, mnc] = op.split("-");
  const of = (m: string | null) => latest(prices.filter((p) => p.channel_id === channelID && p.mcc === mcc && p.mnc === m), now);
  const p = of(mnc) ?? of(null);
  if (!p) return null;
  const rate = latest(rates.filter((r) => r.currency === p.currency), now)?.rate;
  return { price: p.price, currency: p.currency, base: rate === undefined ? null : Math.trunc((p.price * rate) / 1e6) };
}

/** 北京时间的当天分钟数：time 策略的时段按北京时间（docs/10 3.1）。 */
export function beijingMinute(now: number): number {
  return Math.floor(((now / 60_000) + 8 * 60) % (24 * 60));
}

const clock = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));

/** 时段 [start, end) 是否包含这一分钟，终点早于起点表示跨午夜。 */
export function inRange(start: string, end: string, minute: number): boolean {
  const s = clock(start), e = clock(end);
  return s < e ? minute >= s && minute < e : minute >= s || minute < e;
}

/** 时段换成 24 小时轴上的 0 到 1 的片段，跨午夜的拆成两段。 */
export function rangeSpans(start: string, end: string): [number, number][] {
  const s = clock(start) / 1440, e = clock(end) / 1440;
  return s < e ? [[s, e]] : [[s, 1], [0, e]].filter(([a, b]) => b > a) as [number, number][];
}
